<template>
  <div class="casting-alloy-comparison pb-1">
    <div class="cac-filters">
      <v-row class="ma-0" dense>
        <v-col cols="12" md="7" class="py-0 pl-0 pr-2">
          <div class="filterBox" data-tour="predix-comparison-properties">
            <div class="filterBox__title">Select Properties:</div>
            <v-select
              v-model="selectedProperties"
              :items="normalDatasetSelectedGroupFieldsName"
              chips
              clearable
              placeholder="Properties"
              multiple
              outlined
              dense
              hide-details
              :class="['filterBox__select', { 'filterBox__select--has-chips': hasSelectedProperties }]"
              :menu-props="filterMenuProps"
            >
              <template #prepend-inner>
                <i class="fa-solid fa-sliders filterBox__fa-icon" aria-hidden="true"></i>
              </template>
              <template v-slot:prepend-item>
                <v-list-item
                  ripple
                  class="cac-select-all-item"
                  :disabled="!normalDatasetSelectedGroupFieldsName.length"
                  @mousedown.prevent
                  @click="toggleSelectAllProperties"
                >
                  <v-list-item-action class="cac-select-all-item__action">
                    <v-icon :color="allPropertiesSelected || somePropertiesSelected ? 'primary' : undefined">
                      {{ propertiesSelectAllIcon }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Select all</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider></v-divider>
              </template>
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="allPropertiesSelected ? index === 0 : true"
                  small
                  close
                  class="filterBox__chip"
                  @click:close="onRemoveSelectedPropertiesChip(item)"
                >
                  {{ allPropertiesSelected ? "All properties" : item }}
                </v-chip>
              </template>
            </v-select>
          </div>
        </v-col>
        <v-col cols="12" md="5" class="py-0 pl-2 pr-0">
          <div class="filterBox" data-tour="predix-comparison-benchmark-alloy">
            <div class="filterBox__title">Select Benchmark Alloy:</div>
            <div class="cac-benchmark-wrap">
              <v-select
                v-model="selectedStandardAlloyIds"
                :items="benchmarkSelectItems"
                item-text="name"
                item-value="id"
                chips
                clearable
                placeholder="Saved or standard alloy"
                multiple
                outlined
                dense
                hide-details
                :disabled="!hasSelectedProperties"
                :class="['filterBox__select', { 'filterBox__select--has-chips': hasSelectedBenchmarkAlloys }]"
                :menu-props="filterMenuProps"
              >
                <template #prepend-inner>
                  <i class="fa-solid fa-cube filterBox__fa-icon" aria-hidden="true"></i>
                </template>
                <template v-slot:selection="{ item }">
                  <v-chip
                    small
                    close
                    class="filterBox__chip"
                    :class="{ 'filterBox__chip--fav': item.isFavorite }"
                    @click:close="removeStandardAlloyById(item.id)"
                  >
                    <v-icon v-if="item.isFavorite" x-small left>mdi-star</v-icon>
                    {{ item.name }}
                  </v-chip>
                </template>
              </v-select>
              <button
                v-if="!hasSelectedProperties"
                type="button"
                class="cac-benchmark-overlay"
                aria-label="Select properties first to continue"
                @click="showBenchmarkDisabledHint"
              />
              <transition name="cac-hint">
                <div
                  v-if="benchmarkDisabledHintVisible"
                  class="cac-benchmark-hint"
                  role="status"
                >
                  <v-icon small class="cac-benchmark-hint__icon">mdi-information-outline</v-icon>
                  <span>Select properties first to continue.</span>
                </div>
              </transition>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <div class="cac-chart-shell">
      <div
        class="cac-chart-tabs predix-result-tabs"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          class="predix-result-tab cac-chart-tab"
          :class="selectedChartTab === 0 ? 'predix-result-tab--active cac-chart-tab--active' : 'predix-result-tab--idle cac-chart-tab--idle'"
          :aria-selected="selectedChartTab === 0"
          @click="selectChartTab(0)"
        >
          <span
            class="predix-result-tab__icon cac-chart-tab__icon"
            :class="selectedChartTab === 0 ? 'predix-result-tab__icon--active cac-chart-tab__icon--active' : 'predix-result-tab__icon--idle cac-chart-tab__icon--idle'"
          >
            <v-icon small>mdi-chart-bar</v-icon>
          </span>
          Bar Illustration
        </button>
        <button
          type="button"
          role="tab"
          class="predix-result-tab cac-chart-tab"
          :class="selectedChartTab === 1 ? 'predix-result-tab--active cac-chart-tab--active' : 'predix-result-tab--idle cac-chart-tab--idle'"
          :aria-selected="selectedChartTab === 1"
          @click="selectChartTab(1)"
        >
          <span
            class="predix-result-tab__icon cac-chart-tab__icon"
            :class="selectedChartTab === 1 ? 'predix-result-tab__icon--active cac-chart-tab__icon--active' : 'predix-result-tab__icon--idle cac-chart-tab__icon--idle'"
          >
            <v-icon small>mdi-radar</v-icon>
          </span>
          Radar Graph
        </button>
        <button
          type="button"
          role="tab"
          class="predix-result-tab cac-chart-tab"
          :class="selectedChartTab === 2 ? 'predix-result-tab--active cac-chart-tab--active' : 'predix-result-tab--idle cac-chart-tab--idle'"
          :aria-selected="selectedChartTab === 2"
          @click="selectChartTab(2)"
        >
          <span
            class="predix-result-tab__icon cac-chart-tab__icon"
            :class="selectedChartTab === 2 ? 'predix-result-tab__icon--active cac-chart-tab__icon--active' : 'predix-result-tab__icon--idle cac-chart-tab__icon--idle'"
          >
            <v-icon small>mdi-table</v-icon>
          </span>
          Table Compare
        </button>
      </div>

      <div class="cac-chart-card">
        <div class="cac-chart-body">
          <div v-if="selectedChartTab === 0" class="cac-chart-panel">
            <div v-if="!selectedProperties.length" class="cac-empty">
              <div class="cac-empty__icon" aria-hidden="true">
                <v-icon>mdi-chart-bar</v-icon>
              </div>
              <p class="cac-empty__text">Select properties to compare them in a bar chart.</p>
            </div>
            <div v-else class="cac-bar-layout">
              <BarChartLegend class="cac-bar-legend" :series="legendSeries"></BarChartLegend>
              <div class="cac-bar-grid d-flex flex-wrap pb-4 pt-2 justify-start">
                <BarChart
                  class="mt-4"
                  v-for="(prp, idx) in selectedProperties"
                  :key="prp"
                  :propertyName="prp"
                  :ref="'bar' + idx"
                  :minColor="seriesColors.minColor"
                  :maxColor="seriesColors.maxColor"
                  :avgColor="seriesColors.avgColor"
                  :selectedColor="seriesColors.selectedColor"
                  :colors="seriesColors.stdandardColors"
                  :propertyValue="getPropertyValue(prp)"
                  :minValue="getFieldStat(prp, 'min')"
                  :maxValue="getFieldStat(prp, 'max')"
                  :avgValue="getFieldStat(prp, 'avg')"
                  :standardAlloys="selectedStandardAlloyPropsBarChartData[prp] || emptyStandardAlloys"
                ></BarChart>
              </div>
            </div>
          </div>

          <div v-if="selectedChartTab === 1" class="cac-chart-panel cac-chart-panel--radar">
            <div v-if="selectedProperties.length < 3" class="cac-empty">
              <div class="cac-empty__icon" aria-hidden="true">
                <v-icon>mdi-radar</v-icon>
              </div>
              <p class="cac-empty__text">
                {{ selectedProperties.length === 0
                  ? "Select properties to compare them on the radar graph."
                  : "To show the radar graph, at least three properties are required to be selected." }}
              </p>
            </div>
            <div v-else class="cac-radar-wrap">
              <AlloyCompareRadar
                :properties="selectedProperties"
                :series="radarChartSeries"
                ref="radarChart"
              ></AlloyCompareRadar>
            </div>
          </div>

          <div v-if="selectedChartTab === 2" class="cac-chart-panel cac-chart-panel--table">
            <div v-if="!selectedProperties.length" class="cac-empty">
              <div class="cac-empty__icon" aria-hidden="true">
                <v-icon>mdi-table</v-icon>
              </div>
              <p class="cac-empty__text">Select properties to compare them in a table.</p>
            </div>
            <div v-else class="cac-table-scroll">
              <table class="cac-compare-table">
                <thead>
                  <tr>
                    <th class="cac-compare-table__sticky">Property</th>
                    <th class="cac-compare-table__col cac-compare-table__predicted">
                      <span class="cac-table-head">
                        <span class="cac-table-swatch cac-table-swatch--predicted"></span>
                        Predicted
                      </span>
                    </th>
                    <th class="cac-compare-table__col cac-compare-table__min">
                      <span class="cac-table-head">
                        <span class="cac-table-swatch cac-table-swatch--min"></span>
                        Min
                      </span>
                    </th>
                    <th class="cac-compare-table__col cac-compare-table__max">
                      <span class="cac-table-head">
                        <span class="cac-table-swatch cac-table-swatch--max"></span>
                        Max
                      </span>
                    </th>
                    <th class="cac-compare-table__col cac-compare-table__avg">
                      <span class="cac-table-head">
                        <span class="cac-table-swatch cac-table-swatch--avg"></span>
                        Avg
                      </span>
                    </th>
                    <th
                      v-for="alloy in compareTableAlloyColumns"
                      :key="alloy.id"
                    >
                      <span class="cac-table-head" :title="alloy.name">
                        <span class="cac-table-swatch" :style="{ background: alloy.color }"></span>
                        <v-icon v-if="alloy.isFavorite" x-small class="cac-table-fav-icon">mdi-star</v-icon>
                        {{ alloy.name }}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in compareTableRows" :key="row.property">
                    <th class="cac-compare-table__sticky" :title="row.property">{{ row.property }}</th>
                    <td class="cac-compare-table__col cac-compare-table__predicted">{{ formatCompareValue(row.predicted) }}</td>
                    <td class="cac-compare-table__col cac-compare-table__min">{{ formatCompareValue(row.min) }}</td>
                    <td class="cac-compare-table__col cac-compare-table__max">{{ formatCompareValue(row.max) }}</td>
                    <td class="cac-compare-table__col cac-compare-table__avg">{{ formatCompareValue(row.avg) }}</td>
                    <td v-for="alloy in compareTableAlloyColumns" :key="alloy.id">
                      {{ formatCompareValue(row.alloys[alloy.id]) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { getData } from "@/api/user/dataset";
import BarChart from "@/components/Predix/BarChart.vue";
import BarChartLegend from "@/components/Predix/BarChartLegend.vue";
import AlloyCompareRadar from "@/components/Predix/AlloyCompareRadar";
import { smartPruneNumber } from "@/utils/number";

export default {
  components: {
    BarChart,
    BarChartLegend,
    AlloyCompareRadar,
  },
  name: "CastingAlloyComparison",
  props: {
    selectedNormalDatabaseId: {
      type: Number,
      default: -1,
    },
    selectedStandardDatabaseId: {
      type: Number,
      default: -1,
    },
    normalDatabasePropertyData: {
      type: Array,
      default: () => [],
    },
    standardAlloys: {
      type: Array,
      default: () => [],
    },
    favoriteAlloys: {
      type: Array,
      default: () => [],
    },
    package_id: {
      type: Number,
      default: -1,
    },
    normalDatasetFields: {
      type: Array,
      default: () => [],
    },
    fieldGroups: {
      type: Array,
      default: () => [],
    },
    selectedHeatTreatment: {
      type: Number,
      default: -1,
    },
    selectedCastingGroups: {
      type: Number,
      default: -1,
    },
  },
  data: () => ({
    selectedChartTab: 0,
    emptyStandardAlloys: new Set(),
    currentTab: null,
    seriesBaseColors: {
      minColor: "#B6DCDD",
      maxColor: "#D6E1FF",
      avgColor: "#ECDEDB",
      selectedColor: "#038497",
      stdandardColors: [
        { dark: "#D88972", light: "#FEAD94" },
        { dark: "#E9A643", light: "#FFC671" },
        { dark: "#A47CD8", light: "#CAA6F8" },
        { dark: "#BFD08D", light: "#DFEFAF" },
        { dark: "#CBB086", light: "#E8D4B4" },
        { dark: "#AA8FC1", light: "#C4AFD6" },
        { dark: "#EEC4A2", light: "#FCE2CD" },
        { dark: "#97C9DA", light: "#C7E7F2" },
        { dark: "#C585B7", light: "#E3A0D5" },
        { dark: "#DAAFCD", light: "#F4D9EC" },
        { dark: "#93A4DE", light: "#93A4DE" },
        { dark: "#DFC785", light: "#F1DFAD" },
        { dark: "#689879", light: "#92BDA1" },
      ],
    },
    selectedStandardAlloyIds: [],
    standardAlloyProperties: {},
    selectedStandardAlloyProps: {},
    selectedProperties: [],
    benchmarkDisabledHintVisible: false,
    benchmarkDisabledHintTimer: null,
  }),
  computed: {
    filterMenuProps() {
      return {
        bottom: true,
        offsetY: true,
        nudgeBottom: 4,
        maxHeight: 320,
        auto: true,
        contentClass: "filterBox__menu",
      };
    },
    hasSelectedProperties() {
      return this.selectedProperties.length > 0;
    },
    allPropertiesSelected() {
      const names = this.normalDatasetSelectedGroupFieldsName || [];
      return names.length > 0 && names.every((name) => this.selectedProperties.includes(name));
    },
    somePropertiesSelected() {
      return this.selectedProperties.length > 0 && !this.allPropertiesSelected;
    },
    propertiesSelectAllIcon() {
      if (this.allPropertiesSelected) return "mdi-checkbox-marked";
      if (this.somePropertiesSelected) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },
    hasSelectedBenchmarkAlloys() {
      return this.selectedStandardAlloyIds.length > 0;
    },
    benchmarkSelectItems() {
      const items = [{ header: "My Saved Alloys" }];
      const favorites = this.favoriteAlloys || [];
      if (!favorites.length) {
        items.push({
          name: "No saved alloys yet",
          disabled: true,
        });
      } else {
        favorites.forEach((fav) => {
          items.push({
            id: this.favoriteSelectId(fav.id),
            name: this.formatSavedAlloyDisplay(fav.name),
            isFavorite: true,
          });
        });
      }
      items.push({ divider: true });
      items.push({ header: "Standard Alloys" });
      (this.standardAlloys || []).forEach((alloy) => {
        items.push({
          id: alloy.id,
          name: alloy.name,
          isFavorite: false,
        });
      });
      return items;
    },
    seriesColors() {
      let baseColors = this.seriesBaseColors;
      const darkness = "b0";
      if (this.$vuetify.theme.isDark) {
        baseColors.minColor += darkness;
        baseColors.maxColor += darkness;
        baseColors.avgColor += darkness;
        baseColors.selectedColor += darkness;
        baseColors.stdandardColors.forEach((item) => {
          item.dark += darkness;
          item.light += darkness;
        });
      }
      return baseColors;
    },

    normalDatasetSelectedGroupFieldsName() {
      return this.normalDatabasePropertyData
        .filter((x) => x.stats.min !== x.stats.max)
        .map((item) => item.name);
    },
    legendSeries() {
      let x = [
        { name: "Min", color: this.seriesColors.minColor },
        { name: "Max", color: this.seriesColors.maxColor },
        { name: "Avg", color: this.seriesColors.avgColor },
        { name: "Predicted", color: this.seriesColors.selectedColor },
      ];
      this.selectedStandardAlloyIds.forEach((alloyId, index) => {
        x.push({
          name: this.alloyDisplayName(alloyId),
          color: this.seriesColors.stdandardColors[index % this.seriesColors.stdandardColors.length].light,
        });
      });
      return x;
    },
    radarChartSeries() {
      let series = [];
      const selectedAlloy = {
        name: "Predicted",
        data: this.selectedProperties.map((prp) => this.getPropertyValue(prp)),
      };
      series.push(selectedAlloy);

      Object.keys(this.standardAlloyProperties).forEach((alloyId) => {
        const alloyProps = this.standardAlloyProperties[alloyId];
        if (!alloyProps) return;
        const data = this.selectedProperties.map((prp) => {
          const found = alloyProps.find((item) => item.id === prp);
          return found ? found.value : 0;
        });
        series.push({ name: this.alloyDisplayName(alloyId), data });
      });

      const minValues = this.selectedProperties.map((prp) => this.getFieldStat(prp, "min"));
      const maxValues = this.selectedProperties.map((prp) => this.getFieldStat(prp, "max"));

      if (series.length > 1) {
        let min = minValues.slice();
        let max = maxValues.slice();
        for (let i = 0; i < series.length; i++) {
          for (let j = 0; j < series[i].data.length; j++) {
            min[j] = Math.min(series[i].data[j], min[j]);
            max[j] = Math.max(series[i].data[j], max[j]);
          }
        }
        for (let i = 0; i < series.length; i++) {
          for (let j = 0; j < series[i].data.length; j++) {
            if (max[j] === min[j]) {
              series[i].data[j] = series[i].data[j] === 0 ? 0 : 1;
            } else {
              series[i].data[j] = (series[i].data[j] - min[j]) / (max[j] - min[j]);
            }
          }
        }
      } else if (series.length == 1) {
        series[0].data = series[0].data.map((item) => (item > 0 ? 1 : 0)).slice();
      }
      return series;
    },
    selectedStandardAlloyPropsBarChartData() {
      let tmp = {};
      Object.keys(this.standardAlloyProperties).forEach((alloyId) => {
        const alloyProps = this.standardAlloyProperties[alloyId];
        if (!alloyProps) return;
        for (var i = 0; i < this.selectedProperties.length; i++) {
          const propName = this.selectedProperties[i];
          const found = alloyProps.find((item) => item.id === propName);
          if (!found) continue;
          if (!tmp[propName]) {
            tmp[propName] = new Set();
          }
          tmp[propName].add({ name: this.alloyDisplayName(alloyId), value: found.value });
        }
      });
      return tmp;
    },
    compareTableAlloyColumns() {
      const palette = this.seriesColors.stdandardColors;
      return this.selectedStandardAlloyIds.map((alloyId, index) => {
        const swatch = palette[index % palette.length];
        return {
          id: alloyId,
          name: this.alloyDisplayName(alloyId),
          isFavorite: this.isFavoriteSelectId(alloyId),
          color: swatch ? swatch.light : this.seriesColors.selectedColor,
        };
      });
    },
    compareTableRows() {
      return this.selectedProperties.map((prp) => {
        const alloys = {};
        this.selectedStandardAlloyIds.forEach((alloyId) => {
          alloys[alloyId] = this.getStandardAlloyPropertyValue(alloyId, prp);
        });
        return {
          property: prp,
          predicted: this.getPropertyValue(prp),
          min: this.getFieldStat(prp, "min"),
          max: this.getFieldStat(prp, "max"),
          avg: this.getFieldStat(prp, "avg"),
          alloys,
        };
      });
    },
  },
  watch: {
    selectedProperties() {
      if (this.hasSelectedProperties) {
        this.hideBenchmarkDisabledHint();
      }
    },
    favoriteAlloys() {
      (this.selectedStandardAlloyIds || []).forEach((id) => {
        if (this.isFavoriteSelectId(id)) this.hydrateFavoriteProperties(id);
      });
    },
    selectedStandardAlloyIds(newList, oldList) {
      const _this = this;
      if (newList.length > oldList.length) {
        const newlyAddedAlloyId = newList[newList.length - 1];
        if (this.isFavoriteSelectId(newlyAddedAlloyId)) {
          this.hydrateFavoriteProperties(newlyAddedAlloyId);
          return;
        }
        const groups = [_this.selectedCastingGroups, _this.selectedHeatTreatment];
        getData(
          this.package_id,
          this.selectedStandardDatabaseId,
          [{ f0: newlyAddedAlloyId }],
          groups,
          function (res, payload) {
            const alloy = res.result[0];
            const alloyId = payload.filters[0].f0;
            var props = [];
            for (var key of Object.keys(alloy).sort()) {
              props.push({ id: key, value: alloy[key] });
            }
            _this.$set(_this.standardAlloyProperties, alloyId, props);
          }
        );
      } else if (newList.length < oldList.length) {
        const removedAlloyIds = oldList.filter((item) => newList.indexOf(item) < 0);
        removedAlloyIds.forEach((id) => this.$delete(this.standardAlloyProperties, id));
      }
    },
  },

  methods: {
    // Picks up to `count` random, distinct items from `pool` (or all of them
    // if the pool is smaller than `count`).
    pickRandomItems(pool, count) {
      const shuffled = [...pool];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled.slice(0, Math.min(count, shuffled.length));
    },
    favoriteSelectId(id) {
      return `fav::${id}`;
    },
    isFavoriteSelectId(id) {
      return String(id || "").startsWith("fav::");
    },
    favoriteIdFromSelectId(id) {
      return String(id || "").slice(5);
    },
    alloyDisplayName(id) {
      if (this.isFavoriteSelectId(id)) {
        const fav = (this.favoriteAlloys || []).find(
          (item) => String(item.id) === this.favoriteIdFromSelectId(id)
        );
        return fav && fav.name ? this.formatSavedAlloyDisplay(fav.name) : this.favoriteIdFromSelectId(id);
      }
      const alloy = (this.standardAlloys || []).find((item) => item.id === id);
      return alloy && alloy.name ? alloy.name : String(id);
    },
    formatSavedAlloyDisplay(name) {
      const raw = String(name || '');
      const match = raw.match(/^[FS]([1-9]\d{0,6})$/i);
      if (!match) return raw;
      const digits = String(match[1]);
      return `S ${digits.padStart(6, '0')}`;
    },
    hydrateFavoriteProperties(selectId) {
      const favId = this.favoriteIdFromSelectId(selectId);
      const fav = (this.favoriteAlloys || []).find((item) => String(item.id) === String(favId));
      if (!fav) {
        this.$set(this.standardAlloyProperties, selectId, []);
        return;
      }
      const props = (fav.items || [])
        .filter((item) => item && item.kind === "property")
        .map((item) => {
          const n = Number(item.value);
          return { id: item.name, value: Number.isFinite(n) ? n : 0 };
        });
      this.$set(this.standardAlloyProperties, selectId, props);
    },
    selectFirstPropertyForTour() {
      // normalDatasetSelectedGroupFieldsName already filters to properties with a
      // real value range (min !== max), i.e. properties that "have data". Prefer
      // ones whose predicted value is non-zero, and select a few at random.
      const names = this.normalDatasetSelectedGroupFieldsName || [];
      if (!names.length) return;
      const withData = names.filter((n) => !!this.getPropertyValue(n));
      const pool = withData.length ? withData : names;
      this.selectedProperties = this.pickRandomItems(pool, 3);
    },
    selectFirstBenchmarkAlloyForTour() {
      if (!this.standardAlloys || !this.standardAlloys.length) return;
      if (!this.selectedProperties || !this.selectedProperties.length) {
        this.selectFirstPropertyForTour();
      }
      const picks = this.pickRandomItems(this.standardAlloys, 2);
      this.selectedStandardAlloyIds = picks.map((a) => a.id);
    },
    getPropertyValue(prp) {
      const item = this.normalDatabasePropertyData.find((i) => i.name === prp);
      return item ? item.value : 0;
    },
    getFieldStat(prp, stat) {
      // Use the explore result's per-property stats so the chart's Min/Max/Avg
      // match the values shown in the accordion. These come from the dataset the
      // prediction ran against (Benchmark DB distribution on the Benchmark tab,
      // Main DB distribution otherwise).
      const item = this.normalDatabasePropertyData.find((i) => i.name === prp);
      if (item && item.stats && item.stats[stat] !== undefined && item.stats[stat] !== null) {
        return item.stats[stat];
      }
      const field = this.normalDatasetFields.find((i) => i.title === prp);
      return field && field.stats ? field.stats[stat] : 0;
    },
    getStandardAlloyPropertyValue(alloyId, prp) {
      const alloyProps = this.standardAlloyProperties[alloyId];
      if (!alloyProps) return null;
      const found = alloyProps.find((item) => item.id === prp);
      return found && found.value !== undefined && found.value !== null ? found.value : null;
    },
    formatCompareValue(value) {
      if (value === null || value === undefined || value === "") return "—";
      const n = Number(value);
      if (!Number.isFinite(n)) return "—";
      return smartPruneNumber(n);
    },
    selectChartTab(tab) {
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
      if (tab === 0) {
        document.querySelectorAll(".apexcharts-tooltip").forEach((node) => node.remove());
      }
      this.selectedChartTab = tab;
    },
    removeStandardAlloyById(id) {
      const i = this.selectedStandardAlloyIds.indexOf(id);
      if (i !== -1) this.$delete(this.selectedStandardAlloyIds, i);
      this.$delete(this.standardAlloyProperties, id);
    },
    onRemoveSelectedProperties(item) {
      const i = this.selectedProperties.indexOf(item);
      if (i !== -1) this.$delete(this.selectedProperties, i);
    },
    onRemoveSelectedPropertiesChip(item) {
      if (this.allPropertiesSelected) {
        this.selectedProperties = [];
        return;
      }
      this.onRemoveSelectedProperties(item);
    },
    toggleSelectAllProperties() {
      const names = this.normalDatasetSelectedGroupFieldsName || [];
      if (!names.length) return;
      if (this.allPropertiesSelected) {
        this.selectedProperties = [];
        return;
      }
      this.selectedProperties = names.slice();
    },

    onResetProperties() {
      this.normalDatabasePropertyData = [];
      this.selectedStandardAlloyIds = [];
      this.standardAlloyProperties = {};
      this.selectedProperties = [];
    },
    showBenchmarkDisabledHint() {
      this.benchmarkDisabledHintVisible = true;
      if (this.benchmarkDisabledHintTimer) {
        clearTimeout(this.benchmarkDisabledHintTimer);
      }
      this.benchmarkDisabledHintTimer = setTimeout(() => {
        this.hideBenchmarkDisabledHint();
      }, 3200);
    },
    hideBenchmarkDisabledHint() {
      this.benchmarkDisabledHintVisible = false;
      if (this.benchmarkDisabledHintTimer) {
        clearTimeout(this.benchmarkDisabledHintTimer);
        this.benchmarkDisabledHintTimer = null;
      }
    },
  },
  beforeDestroy() {
    this.hideBenchmarkDisabledHint();
  },
};
</script>
<style scoped lang="scss">
.casting-alloy-comparison {
  width: 100%;
  min-width: 0;
  font-family: var(--font-inter), var(--font-sans);
}

.cac-filters {
  padding: 0.75rem 0 0;
}

.cac-bar-layout {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  margin-top: 1rem;
}

.cac-bar-legend {
  flex-shrink: 0;
  align-self: flex-start;
}

.cac-bar-grid {
  flex: 1;
  min-width: 0;
}

.cac-chart-shell {
  margin-top: 0.75rem;
  min-width: 0;
  --cac-chart-surface: var(--color-white);
}

.cac-chart-card {
  padding: 0 0 1.25rem;
  border-radius: 0 0 6px 6px;
  overflow: hidden;
  min-width: 0;
  background-color: var(--cac-chart-surface);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  border: 1px solid var(--color-gray-200);
  border-top: none;
}

.cac-chart-tabs {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  gap: 1.5rem;
  padding: 0 1rem;
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  border: 1px solid var(--color-gray-200);
  border-bottom: 1px solid var(--color-gray-200);
  background-color: var(--cac-chart-surface);
  border-radius: 6px 6px 0 0;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.cac-chart-body {
  position: relative;
  z-index: 1;
  overflow: hidden;
  min-width: 0;
}

.cac-chart-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex: 0 0 auto;
  margin-bottom: -1px;
  padding: 0.75rem 1.25rem;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  font-size: var(--text-sm);
  font-family: inherit;
  line-height: 1.25;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.cac-chart-tab--active {
  color: var(--v-primary-base);
  border-bottom-color: var(--v-primary-base);
  font-weight: 600;
}

.cac-chart-tab--idle {
  color: var(--color-gray-500);
  font-weight: 500;
}

.cac-chart-tab--idle:hover {
  color: var(--color-gray-700);
}

.cac-chart-tab__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
}

::v-deep .cac-chart-tab__icon--active .v-icon {
  color: var(--v-primary-base) !important;
}

::v-deep .cac-chart-tab__icon--idle .v-icon {
  color: var(--color-gray-500) !important;
}

html.dark .cac-chart-shell {
  --cac-chart-surface: var(--color-gray-800);
}

html.dark .cac-chart-card {
  --cac-chart-surface: var(--color-gray-800);
  box-shadow: none;
  border-color: color-mix(in srgb, var(--color-gray-700) 60%, transparent);
}

html.dark .cac-chart-tabs {
  border-color: color-mix(in srgb, var(--color-gray-700) 60%, transparent);
  box-shadow: none;
}

html.dark .cac-chart-tab--idle {
  color: var(--color-gray-400);
}

html.dark .cac-chart-tab--idle:hover {
  color: var(--color-gray-200);
}

html.dark ::v-deep .cac-chart-tab__icon--idle .v-icon {
  color: var(--color-gray-400) !important;
}

html.dark ::v-deep .cac-chart-tab__icon--active .v-icon {
  color: var(--v-primary-base) !important;
}

.cac-chart-panel {
  padding: 0.75rem 1rem 0;
  position: relative;
  z-index: 1;
}

.cac-chart-panel--radar {
  overflow: hidden;
}

.cac-radar-wrap {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  pointer-events: auto;
}

.cac-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 16rem;
  padding: 2rem 1.5rem;
  gap: 0.75rem;
}

.cac-empty__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--v-primary-base) 12%, transparent);
}

.cac-empty__text {
  margin: 0;
  max-width: 22rem;
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--color-gray-600);
}

::v-deep .cac-empty__icon .v-icon {
  color: var(--v-primary-base) !important;
  font-size: 1.5rem !important;
}

.cac-chart-panel--table {
  padding: 0.75rem 1rem 0.25rem;
}

.cac-table-scroll {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  margin: 0.25rem 0 0.5rem;
}

.cac-compare-table {
  --cac-predicted: var(--v-primary-base);
  --cac-min: #fca5a5;
  --cac-max: #86efac;
  --cac-avg: #fcd34d;
  --cac-predicted-tint: var(--v-primary-base);
  --cac-min-tint: #dc2626;
  --cac-max-tint: #16a34a;
  --cac-avg-tint: #d97706;
  width: 100%;
  min-width: 40rem;
  border-collapse: separate;
  border-spacing: 0;
  font-size: var(--text-sm);
  font-family: var(--font-inter), var(--font-sans);
}

.cac-compare-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 0.625rem 0.75rem;
  text-align: left;
  font-weight: 600;
  white-space: nowrap;
  background-color: var(--cac-chart-surface);
  border-bottom: 1px solid var(--color-gray-200);
  color: var(--color-gray-700);
}

.cac-compare-table tbody th,
.cac-compare-table tbody td {
  padding: 0.5625rem 0.75rem;
  border-bottom: 1px solid var(--color-gray-100);
  white-space: nowrap;
  vertical-align: middle;
}

.cac-compare-table tbody th {
  font-weight: 600;
  text-align: left;
  max-width: 16rem;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--color-gray-800);
}

.cac-compare-table tbody td {
  font-variant-numeric: tabular-nums;
  color: var(--color-gray-700);
}

.cac-compare-table tbody tr:hover th,
.cac-compare-table tbody tr:hover td {
  background-color: color-mix(in srgb, var(--color-gray-100) 70%, var(--cac-chart-surface));
}

.cac-compare-table tbody tr:hover td.cac-compare-table__predicted {
  background-color: color-mix(in srgb, var(--cac-predicted-tint) 8%, var(--cac-chart-surface));
}

.cac-compare-table tbody tr:hover td.cac-compare-table__min {
  background-color: color-mix(in srgb, var(--cac-min-tint) 8%, var(--cac-chart-surface));
}

.cac-compare-table tbody tr:hover td.cac-compare-table__max {
  background-color: color-mix(in srgb, var(--cac-max-tint) 8%, var(--cac-chart-surface));
}

.cac-compare-table tbody tr:hover td.cac-compare-table__avg {
  background-color: color-mix(in srgb, var(--cac-avg-tint) 8%, var(--cac-chart-surface));
}

.cac-compare-table__sticky {
  position: sticky;
  left: 0;
  z-index: 3;
  background-color: var(--cac-chart-surface);
  box-shadow: 1px 0 0 0 var(--color-gray-200);
}

.cac-compare-table thead .cac-compare-table__sticky {
  z-index: 4;
}

.cac-compare-table thead th.cac-compare-table__predicted,
.cac-compare-table tbody td.cac-compare-table__predicted {
  background-color: color-mix(in srgb, var(--cac-predicted-tint) 5%, var(--cac-chart-surface));
}

.cac-compare-table thead th.cac-compare-table__min,
.cac-compare-table tbody td.cac-compare-table__min {
  background-color: color-mix(in srgb, var(--cac-min-tint) 5%, var(--cac-chart-surface));
}

.cac-compare-table thead th.cac-compare-table__max,
.cac-compare-table tbody td.cac-compare-table__max {
  background-color: color-mix(in srgb, var(--cac-max-tint) 5%, var(--cac-chart-surface));
}

.cac-compare-table thead th.cac-compare-table__avg,
.cac-compare-table tbody td.cac-compare-table__avg {
  background-color: color-mix(in srgb, var(--cac-avg-tint) 5%, var(--cac-chart-surface));
}

.cac-compare-table thead th.cac-compare-table__min,
.cac-compare-table tbody td.cac-compare-table__min,
.cac-compare-table thead th.cac-compare-table__max,
.cac-compare-table tbody td.cac-compare-table__max,
.cac-compare-table thead th.cac-compare-table__avg,
.cac-compare-table tbody td.cac-compare-table__avg {
  color: var(--color-gray-900);
}

.cac-compare-table__predicted {
  font-weight: 600;
  color: var(--cac-predicted);
}

.cac-compare-table tbody td.cac-compare-table__predicted {
  color: var(--cac-predicted);
  font-weight: 700;
}

.cac-table-head {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cac-table-swatch {
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 999px;
  flex-shrink: 0;
}

::v-deep .cac-table-fav-icon.v-icon {
  color: var(--v-primary-base) !important;
  font-size: 0.875rem !important;
  margin-right: -0.125rem;
}

.cac-table-swatch--predicted {
  background-color: var(--cac-predicted);
}

.cac-table-swatch--min {
  background-color: var(--cac-min);
}

.cac-table-swatch--max {
  background-color: var(--cac-max);
}

.cac-table-swatch--avg {
  background-color: var(--cac-avg);
}

.filterBox {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 0 0 0.75rem;
  border: none;
  border-radius: 0;
  background: transparent;
  min-height: 0;
}

.cac-benchmark-wrap {
  position: relative;
}

.cac-benchmark-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.cac-benchmark-hint {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 0.375rem);
  z-index: 5;
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  padding: 0.5rem 0.625rem;
  border-radius: var(--radius-lg);
  background-color: var(--color-white);
  border: 1px solid var(--color-gray-200);
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  font-size: var(--text-sm);
  line-height: 1.4;
  color: var(--color-gray-700);
}

::v-deep .cac-benchmark-hint__icon.v-icon {
  color: var(--v-primary-base) !important;
  margin-top: 0.0625rem;
}

.cac-hint-enter-active,
.cac-hint-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.cac-hint-enter,
.cac-hint-leave-to {
  opacity: 0;
  transform: translateY(-0.25rem);
}

.filterBox__title {
  font-size: var(--text-sm);
  font-weight: 600;
  font-family: var(--font-inter), var(--font-sans);
  letter-spacing: 0.01em;
}

.filterBox__fa-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  min-width: 1rem;
  font-size: 0.875rem;
  line-height: 1;
  flex-shrink: 0;
  pointer-events: none;
}

::v-deep .filterBox__select.v-text-field--outlined {
  margin: 0;
  padding: 0;

  > .v-input__control {
    align-items: center;
  }

  > .v-input__control > .v-input__slot {
    min-height: 2.5rem !important;
    display: flex !important;
    align-items: center !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  &.v-input--is-label-active > .v-input__control > .v-input__slot {
    min-height: 2.5rem !important;
    height: auto !important;
    align-items: center !important;
    padding-top: 0.375rem !important;
    padding-bottom: 0.375rem !important;
  }

  &.filterBox__select--has-chips .v-label {
    display: none !important;
  }

  &.filterBox__select--has-chips fieldset legend {
    max-width: 0.01px !important;
    width: 0.01px !important;
    padding: 0 !important;
    overflow: hidden;
  }

  &.filterBox__select--has-chips fieldset legend span {
    display: none !important;
    padding: 0 !important;
  }

  fieldset {
    border-radius: var(--radius-lg) !important;
  }

  &.v-input--is-focused fieldset {
    border-width: 1px !important;
  }

  .v-label {
    font-size: var(--text-sm);
    font-family: var(--font-inter), var(--font-sans);
  }

  &.v-text-field--outlined.v-input--dense:not(.v-input--is-label-active) .v-label {
    top: 50% !important;
    transform: translateY(-50%) !important;
  }

  &.v-text-field--outlined.v-input--dense.v-input--is-label-active:not(.filterBox__select--has-chips) .v-label {
    top: 0.625rem !important;
    transform: translateY(0) scale(0.75) !important;
    transform-origin: left top;
  }

  .v-select__slot,
  .v-combobox__slot {
    align-items: center;
  }

  .v-select__selections,
  .v-combobox__selection {
    gap: 0.25rem;
    min-height: 1.5rem;
    max-height: 5.5rem;
    overflow-y: auto;
    align-items: center;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    flex-wrap: wrap;
  }

  .v-select__selections input {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
    min-width: 0 !important;
    padding: 0 !important;
  }

  .v-input__prepend-inner,
  .v-input__append-inner {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    align-self: center !important;
  }

  .v-input__prepend-inner {
    /* The outlined fieldset is absolutely positioned and painted with an opaque
       fill, so the prepend area needs its own stacking position to stay visible. */
    position: relative;
    z-index: 1;
    flex: 0 0 auto !important;
    flex-shrink: 0 !important;
    margin-right: 0 !important;
    padding-right: 0.5rem !important;
    display: inline-flex !important;
    align-items: center !important;
    width: auto !important;
    min-width: 1rem !important;
    overflow: visible !important;
  }

  .v-input__append-inner {
    position: relative;
    z-index: 1;
    flex: 0 0 auto !important;
    flex-shrink: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    gap: 0.25rem;
    margin-left: 0.25rem !important;
    overflow: visible !important;
  }

  .v-select__slot {
    width: auto !important;
    flex: 1 1 auto !important;
    min-width: 0 !important;
    max-width: none !important;
  }
}

::v-deep .filterBox__chip.v-chip {
  font-size: 0.6875rem;
  font-weight: 600;
  height: 1.375rem;
}

::v-deep .filterBox__chip--fav.v-chip {
  background-color: color-mix(in srgb, var(--v-primary-base) 18%, var(--color-white)) !important;
}

::v-deep .cac-benchmark-item-icon {
  min-width: 1.5rem;
  margin-right: 0.25rem;
}

::v-deep .filterBox__chip.v-chip::before {
  opacity: 0 !important;
}

::v-deep .filterBox__chip.v-chip:focus,
::v-deep .filterBox__chip.v-chip--active,
::v-deep .filterBox__chip.v-chip--selected {
  outline: none !important;
  box-shadow: none !important;
}
</style>

<style lang="scss">
/* Theme — match Alloy Predictions expansion panels / CastingExploreResult */
.v-application.theme--light .casting-alloy-comparison .filterBox__title {
  color: var(--color-gray-700);
}

.v-application.theme--light .casting-alloy-comparison .filterBox__fa-icon {
  color: var(--color-gray-500);
}

html.dark .casting-alloy-comparison .filterBox__title,
.v-application.theme--dark .casting-alloy-comparison .filterBox__title {
  color: var(--color-gray-300);
}

html.dark .casting-alloy-comparison .cac-benchmark-hint,
.v-application.theme--dark .casting-alloy-comparison .cac-benchmark-hint {
  background-color: var(--color-gray-800);
  border-color: color-mix(in srgb, var(--color-gray-600) 70%, transparent);
  box-shadow: 0 4px 12px rgb(0 0 0 / 0.45);
  color: var(--color-gray-200);
}

html.dark .casting-alloy-comparison .filterBox__fa-icon,
.v-application.theme--dark .casting-alloy-comparison .filterBox__fa-icon {
  color: var(--color-gray-400);
}

html.dark .casting-alloy-comparison .cac-chart-card,
.v-application.theme--dark .casting-alloy-comparison .cac-chart-card {
  --cac-chart-surface: var(--color-gray-800);
  box-shadow: none;
}

html.dark .casting-alloy-comparison .cac-chart-tabs,
.v-application.theme--dark .casting-alloy-comparison .cac-chart-tabs {
  border-bottom-color: color-mix(in srgb, var(--color-gray-700) 60%, transparent);
}

html.dark .casting-alloy-comparison .cac-chart-tab--idle,
.v-application.theme--dark .casting-alloy-comparison .cac-chart-tab--idle {
  color: var(--color-gray-400);
}

html.dark .casting-alloy-comparison .cac-chart-tab--idle:hover,
.v-application.theme--dark .casting-alloy-comparison .cac-chart-tab--idle:hover {
  color: var(--color-gray-200);
}

html.dark .casting-alloy-comparison .cac-chart-tab__icon--idle .v-icon,
.v-application.theme--dark .casting-alloy-comparison .cac-chart-tab__icon--idle .v-icon {
  color: var(--color-gray-400) !important;
}

html.dark .casting-alloy-comparison .cac-chart-tab__icon--active .v-icon,
.v-application.theme--dark .casting-alloy-comparison .cac-chart-tab__icon--active .v-icon {
  color: var(--v-primary-base) !important;
}

html.dark .casting-alloy-comparison .cac-empty__text,
.v-application.theme--dark .casting-alloy-comparison .cac-empty__text {
  color: var(--color-gray-400);
}

html.dark .casting-alloy-comparison .cac-empty__icon,
.v-application.theme--dark .casting-alloy-comparison .cac-empty__icon {
  background: color-mix(in srgb, var(--v-primary-base) 18%, transparent);
}

html.dark .casting-alloy-comparison .cac-compare-table thead th,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table thead th {
  border-bottom-color: color-mix(in srgb, var(--color-gray-700) 60%, transparent);
  color: #fff;
}

html.dark .casting-alloy-comparison .cac-compare-table tbody th,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody th,
html.dark .casting-alloy-comparison .cac-compare-table tbody td,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody td {
  border-bottom-color: color-mix(in srgb, var(--color-gray-700) 45%, transparent);
}

html.dark .casting-alloy-comparison .cac-compare-table tbody th,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody th,
html.dark .casting-alloy-comparison .cac-compare-table tbody td,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody td {
  color: #fff;
}

html.dark .casting-alloy-comparison .cac-compare-table__sticky,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table__sticky {
  box-shadow: 1px 0 0 0 color-mix(in srgb, var(--color-gray-700) 60%, transparent);
}

html.dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__predicted,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__predicted,
html.dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__predicted,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__predicted {
  background-color: color-mix(in srgb, var(--v-primary-base) 8%, var(--color-gray-800));
  color: #fff;
  font-weight: 700;
}

html.dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__min,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__min,
html.dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__min,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__min,
html.dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__max,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__max,
html.dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__max,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__max,
html.dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__avg,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__avg,
html.dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__avg,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__avg {
  color: #fff;
}

html.dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__min,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__min,
html.dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__min,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__min {
  background-color: color-mix(in srgb, #dc2626 8%, var(--color-gray-800));
}

html.dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__max,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__max,
html.dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__max,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__max {
  background-color: color-mix(in srgb, #16a34a 8%, var(--color-gray-800));
}

html.dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__avg,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table thead th.cac-compare-table__avg,
html.dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__avg,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody td.cac-compare-table__avg {
  background-color: color-mix(in srgb, #d97706 8%, var(--color-gray-800));
}

html.dark .casting-alloy-comparison .cac-compare-table tbody tr:hover th,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody tr:hover th,
html.dark .casting-alloy-comparison .cac-compare-table tbody tr:hover td,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody tr:hover td {
  background-color: color-mix(in srgb, var(--color-gray-700) 40%, var(--color-gray-800));
}

html.dark .casting-alloy-comparison .cac-compare-table tbody tr:hover td.cac-compare-table__predicted,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody tr:hover td.cac-compare-table__predicted {
  background-color: color-mix(in srgb, var(--v-primary-base) 12%, var(--color-gray-800));
}

html.dark .casting-alloy-comparison .cac-compare-table tbody tr:hover td.cac-compare-table__min,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody tr:hover td.cac-compare-table__min {
  background-color: color-mix(in srgb, #dc2626 12%, var(--color-gray-800));
}

html.dark .casting-alloy-comparison .cac-compare-table tbody tr:hover td.cac-compare-table__max,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody tr:hover td.cac-compare-table__max {
  background-color: color-mix(in srgb, #16a34a 12%, var(--color-gray-800));
}

html.dark .casting-alloy-comparison .cac-compare-table tbody tr:hover td.cac-compare-table__avg,
.v-application.theme--dark .casting-alloy-comparison .cac-compare-table tbody tr:hover td.cac-compare-table__avg {
  background-color: color-mix(in srgb, #d97706 12%, var(--color-gray-800));
}

.casting-alloy-comparison .filterBox__select.filterBox__select--has-chips fieldset legend {
  max-width: 0 !important;
  width: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.casting-alloy-comparison .filterBox__select.filterBox__select--has-chips fieldset legend span {
  display: none !important;
  padding: 0 !important;
}

.v-application.theme--light .casting-alloy-comparison .filterBox__select.v-text-field--outlined > .v-input__control > .v-input__slot {
  background-color: var(--color-white) !important;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.v-application.theme--light .casting-alloy-comparison .filterBox__select.v-text-field--outlined fieldset {
  border-color: var(--color-gray-200) !important;
  /* Paint the fill on the fieldset (the bordered box) so the background always
     matches the border rectangle exactly, regardless of the label-notch offset. */
  background-color: var(--color-white) !important;
}

.v-application.theme--light .casting-alloy-comparison .filterBox__select.v-text-field--outlined:hover fieldset,
.v-application.theme--light .casting-alloy-comparison .filterBox__select.v-text-field--outlined.v-input--is-focused fieldset {
  border-color: var(--color-gray-300) !important;
}

.v-application.theme--light .casting-alloy-comparison .filterBox__select .v-label {
  color: var(--color-gray-500) !important;
}

.v-application.theme--light .casting-alloy-comparison .filterBox__select .v-input__append-inner .v-icon {
  color: var(--color-gray-500) !important;
}

.v-application.theme--light .casting-alloy-comparison .filterBox__select .v-select__selections,
.v-application.theme--light .casting-alloy-comparison .filterBox__select .v-select__selection,
.v-application.theme--light .casting-alloy-comparison .filterBox__select .v-combobox__selection {
  color: var(--color-gray-800) !important;
}

.v-application.theme--light .casting-alloy-comparison .filterBox__chip.v-chip {
  background-color: color-mix(in srgb, var(--v-primary-base) 12%, var(--color-white)) !important;
  color: var(--color-gray-800) !important;
}

html.dark .casting-alloy-comparison .filterBox__select.v-text-field--outlined > .v-input__control > .v-input__slot,
.v-application.theme--dark .casting-alloy-comparison .filterBox__select.v-text-field--outlined > .v-input__control > .v-input__slot {
  background-color: color-mix(in srgb, var(--color-gray-900) 45%, var(--color-gray-800)) !important;
  box-shadow: none;
}

html.dark .casting-alloy-comparison .filterBox__select.v-text-field--outlined fieldset,
.v-application.theme--dark .casting-alloy-comparison .filterBox__select.v-text-field--outlined fieldset {
  border-color: color-mix(in srgb, var(--color-gray-600) 70%, transparent) !important;
  /* Paint the fill on the fieldset (the bordered box) so the background always
     matches the border rectangle exactly, regardless of the label-notch offset. */
  background-color: color-mix(in srgb, var(--color-gray-900) 45%, var(--color-gray-800)) !important;
}

html.dark .casting-alloy-comparison .filterBox__select.v-text-field--outlined:hover fieldset,
.v-application.theme--dark .casting-alloy-comparison .filterBox__select.v-text-field--outlined:hover fieldset,
html.dark .casting-alloy-comparison .filterBox__select.v-text-field--outlined.v-input--is-focused fieldset,
.v-application.theme--dark .casting-alloy-comparison .filterBox__select.v-text-field--outlined.v-input--is-focused fieldset {
  border-color: var(--color-gray-500) !important;
}

html.dark .casting-alloy-comparison .filterBox__select .v-label,
.v-application.theme--dark .casting-alloy-comparison .filterBox__select .v-label {
  color: var(--color-gray-400) !important;
}

html.dark .casting-alloy-comparison .filterBox__select .v-input__append-inner .v-icon,
.v-application.theme--dark .casting-alloy-comparison .filterBox__select .v-input__append-inner .v-icon {
  color: var(--color-gray-400) !important;
}

html.dark .casting-alloy-comparison .filterBox__select .v-select__selections,
.v-application.theme--dark .casting-alloy-comparison .filterBox__select .v-select__selections,
html.dark .casting-alloy-comparison .filterBox__select .v-select__selection,
.v-application.theme--dark .casting-alloy-comparison .filterBox__select .v-select__selection,
html.dark .casting-alloy-comparison .filterBox__select .v-combobox__selection,
.v-application.theme--dark .casting-alloy-comparison .filterBox__select .v-combobox__selection {
  color: var(--color-gray-100) !important;
}

html.dark .casting-alloy-comparison .filterBox__chip.v-chip,
.v-application.theme--dark .casting-alloy-comparison .filterBox__chip.v-chip {
  background-color: color-mix(in srgb, var(--v-primary-base) 22%, var(--color-gray-800)) !important;
  color: var(--color-gray-100) !important;
}

html.dark .casting-alloy-comparison .filterBox__chip.v-chip .v-chip__close,
.v-application.theme--dark .casting-alloy-comparison .filterBox__chip.v-chip .v-chip__close {
  color: var(--color-gray-300) !important;
}

html.dark .casting-alloy-comparison .filterBox__select.v-text-field--outlined.theme--light > .v-input__control > .v-input__slot,
.v-application.theme--dark .casting-alloy-comparison .filterBox__select.v-text-field--outlined.theme--light > .v-input__control > .v-input__slot {
  background-color: color-mix(in srgb, var(--color-gray-900) 45%, var(--color-gray-800)) !important;
}

html.dark .casting-alloy-comparison .filterBox__select.v-text-field--outlined.theme--light fieldset,
.v-application.theme--dark .casting-alloy-comparison .filterBox__select.v-text-field--outlined.theme--light fieldset {
  border-color: color-mix(in srgb, var(--color-gray-600) 70%, transparent) !important;
  background-color: color-mix(in srgb, var(--color-gray-900) 45%, var(--color-gray-800)) !important;
}

.filterBox__menu.v-menu__content {
  overflow-y: auto !important;
}

.filterBox__menu .cac-select-all-item .v-list-item__action.cac-select-all-item__action {
  min-width: 1.5rem;
  margin: 0 0.5rem 0 0 !important;
  align-self: center;
}

.filterBox__menu .cac-select-all-item .v-list-item__action.cac-select-all-item__action .v-icon {
  font-size: 1.25rem;
}

.filterBox__menu .v-list-item--disabled .v-list-item__action {
  display: none !important;
}

/* —— dropdown menu (teleported) — theme aware —— */
.v-application.theme--light .filterBox__menu {
  margin-top: 0.25rem !important;
  border: 1px solid var(--color-gray-200) !important;
  border-radius: var(--radius-lg) !important;
  background-color: var(--color-white) !important;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1) !important;
  overflow: hidden;
}

.v-application.theme--light .filterBox__menu .v-list {
  padding: 0.25rem 0 !important;
  background: transparent !important;
}

.v-application.theme--light .filterBox__menu .v-list-item {
  min-height: 2.25rem !important;
  padding-inline: 0.75rem !important;
  font-size: var(--text-sm) !important;
  font-family: var(--font-inter), var(--font-sans) !important;
  color: var(--color-gray-800) !important;
}

.v-application.theme--light .filterBox__menu .v-list-item::before {
  border-radius: var(--radius-md);
}

.v-application.theme--light .filterBox__menu .v-list-item:hover {
  background-color: var(--color-gray-50) !important;
}

.v-application.theme--light .filterBox__menu .v-list-item--active {
  background-color: color-mix(in srgb, var(--v-primary-base) 10%, var(--color-white)) !important;
  color: var(--color-gray-900) !important;
}

.v-application.theme--light .filterBox__menu .v-list-item__title {
  font-size: var(--text-sm) !important;
  font-weight: 500;
}

.v-application.theme--light .filterBox__menu .v-subheader {
  height: 2rem !important;
  padding: 0 0.75rem !important;
  font-size: 0.6875rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-gray-500) !important;
}

html.dark .filterBox__menu,
.v-application.theme--dark .filterBox__menu,
html.dark .filterBox__menu.theme--light,
.v-application.theme--dark .filterBox__menu.theme--light {
  margin-top: 0.25rem !important;
  border: 1px solid color-mix(in srgb, var(--color-gray-600) 70%, transparent) !important;
  border-radius: var(--radius-lg) !important;
  background-color: var(--color-gray-800) !important;
  box-shadow: 0 4px 12px rgb(0 0 0 / 0.45) !important;
  overflow: hidden;
}

html.dark .filterBox__menu .v-list,
.v-application.theme--dark .filterBox__menu .v-list,
html.dark .filterBox__menu.theme--light .v-list,
.v-application.theme--dark .filterBox__menu.theme--light .v-list {
  padding: 0.25rem 0 !important;
  background: transparent !important;
}

html.dark .filterBox__menu .v-list-item,
.v-application.theme--dark .filterBox__menu .v-list-item,
html.dark .filterBox__menu.theme--light .v-list-item,
.v-application.theme--dark .filterBox__menu.theme--light .v-list-item {
  min-height: 2.25rem !important;
  padding-inline: 0.75rem !important;
  font-size: var(--text-sm) !important;
  font-family: var(--font-inter), var(--font-sans) !important;
  color: var(--color-gray-200) !important;
}

html.dark .filterBox__menu .v-list-item::before,
.v-application.theme--dark .filterBox__menu .v-list-item::before,
html.dark .filterBox__menu.theme--light .v-list-item::before,
.v-application.theme--dark .filterBox__menu.theme--light .v-list-item::before {
  border-radius: var(--radius-md);
}

html.dark .filterBox__menu .v-list-item:hover,
.v-application.theme--dark .filterBox__menu .v-list-item:hover,
html.dark .filterBox__menu.theme--light .v-list-item:hover,
.v-application.theme--dark .filterBox__menu.theme--light .v-list-item:hover {
  background-color: color-mix(in srgb, var(--color-gray-700) 45%, transparent) !important;
}

html.dark .filterBox__menu .v-list-item--active,
.v-application.theme--dark .filterBox__menu .v-list-item--active,
html.dark .filterBox__menu.theme--light .v-list-item--active,
.v-application.theme--dark .filterBox__menu.theme--light .v-list-item--active {
  background-color: color-mix(in srgb, var(--v-primary-base) 22%, var(--color-gray-800)) !important;
  color: var(--color-gray-100) !important;
}

html.dark .filterBox__menu .v-list-item__title,
.v-application.theme--dark .filterBox__menu .v-list-item__title,
html.dark .filterBox__menu.theme--light .v-list-item__title,
.v-application.theme--dark .filterBox__menu.theme--light .v-list-item__title {
  font-size: var(--text-sm) !important;
  font-weight: 500;
  color: inherit !important;
}

html.dark .filterBox__menu .v-subheader,
.v-application.theme--dark .filterBox__menu .v-subheader,
html.dark .filterBox__menu.theme--light .v-subheader,
.v-application.theme--dark .filterBox__menu.theme--light .v-subheader {
  height: 2rem !important;
  padding: 0 0.75rem !important;
  font-size: 0.6875rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-gray-400) !important;
}

html.dark .casting-alloy-comparison .filterBox__chip--fav.v-chip,
.v-application.theme--dark .casting-alloy-comparison .filterBox__chip--fav.v-chip {
  background-color: color-mix(in srgb, var(--v-primary-base) 28%, var(--color-gray-800)) !important;
}
</style>
